import React, {useEffect, useRef, useState} from 'react';
import {
	Search,
	Download,
	ChevronDown,
	ChevronUp,
	Check,
	Clock,
	ChevronLeft,
	ChevronRight, ArrowDown, ArrowUp, Loader2,
} from 'lucide-react';
import useApi from '../../../utils/useApi';
import LoadingScreen from '../../LoadingScreen/LoadingScreen';
import Button from '../../UI/Button';
import ReportFilters from './ReportFilters';
import {Link, useSearchParams} from 'react-router-dom';
import FilterPills from './FilterPills';

const Report = ({ user, handleShowHeader, handleShowSettingsHeader }) => {
	const { getReport } = useApi();
	const [searchParams] = useSearchParams();
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [headerText, setHeaderText] = useState('Report');
	const [activeFilter, setActiveFilter] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const searchTimeout = useRef(null);
	const [filters, setFilters] = useState(null);
	const [selectedFilters, setSelectedFilters] = useState(null);
	const [expandedClients, setExpandedClients] = useState(new Set());
	// Sorting states
	const [sortField, setSortField] = useState(null);
	const [sortDirection, setSortDirection] = useState(null);
	const [pagination, setPagination] = useState({
		                                             currentPage: 1,
		                                             totalPages: 1,
		                                             total: 0,
		                                             perPage: 10,
	                                             });

	const toggleClientExpansion = (clientId) => {
		const newExpanded = new Set(expandedClients);
		if (expandedClients.has(clientId)) {
			newExpanded.delete(clientId);
		} else {
			newExpanded.add(clientId);
		}
		setExpandedClients(newExpanded);
	};

	useEffect(() => {
		handleShowHeader(true);
		handleShowSettingsHeader(false);

		// Grab filters from the URL params on initial load
		const urlFilters = {};
		searchParams.forEach((value, key) => {
			if (value) {
				urlFilters[key] = value;
			}
		});

		// If there are any URL filters, apply them
		initialize(1, Object.keys(urlFilters).length > 0 ? urlFilters : undefined);
	}, []);

	const initialize = async (page = 1, newFilters) => {
		setIsLoading(true);
		try {
			const response = await getReport(page, newFilters);
			setData(response.users.data);

			// If newFilters were passed in, merge them with the API's selectedFilters
			if (newFilters) {
				setSelectedFilters({ ...response.selectedFilters, ...newFilters });
			} else {
				setSelectedFilters(response.selectedFilters);
			}

			setFilters(response.allFilters);
			setPagination({
				              currentPage: response.users.current_page,
				              totalPages: response.users.last_page,
				              total: response.users.total,
				              perPage: response.users.per_page,
			              });
		}
		catch (error) {
			console.error('Error fetching report:', error);
		}
		finally {
			setIsLoading(false);
		}
	};

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= pagination.totalPages) {
			initialize(newPage, selectedFilters);
		}
	};

	const handleSearch = (event) => {
		const value = event.target.value;
		setSearchTerm(value);

		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		searchTimeout.current = setTimeout(() => {
			handleSearchData(value);
		}, 300);
	};

	const handleSearchData = async (term) => {
		const response = await getReport(1, selectedFilters, term);
		setData(response.users.data);
		setPagination({
			              currentPage: response.users.current_page,
			              totalPages: response.users.last_page,
			              total: response.users.total,
			              perPage: response.users.per_page,
		              });
	};

	/**
	 * Removes all *quick filter* keys from existing filters, preserves filters from ReportFilters (like dates).
	 * Then merges in the newly selected quick filter.
	 */
	const handleApplyFilters = async (newFilters, buttonText = '', sorting) => {
		setIsLoading(true);
		try {
			// Clone existing filters first
			const existingFilters = { ...selectedFilters };

			if (!sorting) {
				// Remove any quick filter keys from existing filters
				quickFilters.forEach((qf) => {
					Object.keys(qf.filters).forEach((k) => {
						delete existingFilters[k];
					});
				});

			}

			// Now, merge the newly selected quick filter into the existing filters
			let mergedFilters = {
				...existingFilters,
				...newFilters,
				...sorting
			};

			// If a quick filter button is pressed, update the header
			if (buttonText) {
				// e.g., "Extension Needed" => "Extension Needed Report"
				const newBtnText = buttonText.replace('Report', '').trim();
				setHeaderText(`${newBtnText} Report`);
				setActiveFilter(JSON.stringify(newFilters));
			}

			const response = await getReport(1, mergedFilters);
			setData(response.users.data);
			setSelectedFilters(mergedFilters);
			setPagination({
				              currentPage: response.users.current_page,
				              totalPages: response.users.last_page,
				              total: response.users.total,
				              perPage: response.users.per_page,
			              });
		}
		catch (error) {
			console.error('Error applying filters:', error);
		}
		finally {
			setIsLoading(false);
		}
	};

	// Handle sort toggle
	const handleSort = (field) => {
		let newDirection = null;
		// If clicking on the same field, toggle direction
		if (field === sortField) {
			if (!sortDirection) {
				newDirection = 'asc';
			} else {
				newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
			}

			setSortDirection(newDirection);
		} else {
			newDirection = 'asc';
			// If clicking on a different field, set it as the new sort field and default to ascending
			setSortField(field);
			setSortDirection('asc');
		}

		// Reset page and fetch clients with new sorting

		setData([]); // Clear existing clients
		handleApplyFilters({
			                   'order_dir': newDirection,
			                   'order_by': field,
		                   },
		                   '',
		                   true //sorting
		);
	};

	/**
	 * Called by the FilterPills component whenever the user removes a single filter pill.
	 * We set that filter’s value to null (or delete it) and re-run the report.
	 */
	const handleRemoveFilter = async (key) => {
		const updatedFilters = { ...selectedFilters };
		updatedFilters[key] = null; // or delete updatedFilters[key];

		setSelectedFilters(updatedFilters);
		initialize(1, updatedFilters);
	};

	const createDataGroups = (client) => ({
		essentialInfo: {
			title: null,
			data: [
				{ label: 'Client Name', value: client.profile.client },
				{ label: 'SSN/Tax ID', value: client.profile.ssn_id },
				{ label: 'Account Type', value: client.profile.account_type },
				{
					label: 'Tax Preparer',
					value:
						client.client_progress.tax_preparer?.profile?.client ?? 'N/A',
				},
				{
					label: 'Extension Needed',
					value: client.client_progress.extension_needed === 1 ? 'Yes' : 'No',
				},
				{
					label: 'Tax Complete',
					value: client.client_progress.tax_complete === 1 ? 'Yes' : 'No',
				},
			],
		},
		taxInformation: {
			title: 'Tax Information',
			data: [
				{ label: 'State Return', value: client.profile.st_return ? 'Yes' : 'No' },
				{ label: 'State', value: client.profile.st_return || 'N/A' },
				{ label: 'Software Used', value: client.client_progress.software_used },
				{
					label: 'LLC Included',
					value:
						client.client_progress.return_includes_llc === 1 ? 'Yes' : 'No',
				},
				{
					label: 'Picked Up Return',
					value: client.client_progress.picked_up_return === 1 ? 'Yes' : 'No',
				},
				{
					label: 'Picked Up Return Date',
					value: client.client_progress.picked_up_return_date,
				},
			],
		},
		spouseInformation: {
			title: 'Spouse Information',
			data: [
				{ label: 'Name', value: client.profile.spouse_name },
				{ label: 'SSN', value: client.profile.spouse_ssn },
				{ label: 'DOB', value: client.profile.spouse_dob },
			],
		},
		progressTracking: {
			title: 'Progress Tracking',
			data: [
				{
					label: 'Documents Uploaded Date',
					value: client.client_progress.documents_uploaded_date,
				},
				{
					label: 'Ready Date',
					value: client.client_progress.ready_for_preparer_date,
				},
				{
					label: 'Tax Started',
					value: client.client_progress.tax_started ? 'Yes' : 'No',
				},
				{
					label: 'Tax Started/Documents Received Date',
					value: client.client_progress.tax_started_date,
				},
				{
					label: 'Extension Filed',
					value: client.client_progress.extension_filed_date,
				},
				{
					label: 'Return Submitted',
					value: client.client_progress.date_return,
				},
				{
					label: 'Incomplete Date',
					value: client.client_progress.tax_incomplete_date,
				},
				{
					label: 'IRS Acceptance',
					value: client.client_progress.irs_acceptance_date,
				},
			],
		},
		franchiseInfo: {
			title: 'Franchise Information',
			data: [
				{
					label: 'Franchise Report',
					value: client.client_progress.franchise_report === 1 ? 'Yes' : 'No',
				},
				{
					label: 'Report Filed Date',
					value: client.client_progress.franchise_report_filed_date,
				},
				{
					label: 'Report Initials',
					value: client.client_progress.franchise_report_initials,
				},
			],
		},
		financialDetails: {
			title: 'Financial Details',
			data: [
				{ label: 'Refund Amount', value: client.client_progress.refund_amount },
				{
					label: 'State Refund',
					value: client.client_progress.state_refund_amount,
				},
				{
					label: 'Prep Fee',
					value: client.client_progress.tax_prep_fee,
				},
				{
					label: 'Additional Fees',
					value: client.client_progress.addt_fees,
				},
				{
					label: 'Abatement Fee',
					value: client.client_progress.abatement_fee,
				},
				{
					label: 'Bookkeeping Fee',
					value: client.client_progress.bookkeeping_fee,
				},
				{
					label: 'Extension Fee',
					value: client.client_progress.extension_fee,
				},
				{
					label: 'Franchise Fee',
					value: client.client_progress.franchise_fee,
				},
				{
					label: 'Credit Card Fee',
					value: client.client_progress.credit_card_fee,
				},
				{
					label: 'Payment Method',
					value: client.client_progress.payment_method,
				},
				{
					label: 'Payment Made',
					value: client.client_progress.payment_made ? 'Yes' : 'No',
				},
				{
					label: 'Payment Date',
					value: client.client_progress.payment_made_date,
				},
			],
		},
		clientBackground: {
			title: 'Client Background',
			data: [
				{ label: 'New Client', value: client.profile.new_client },
				{ label: 'Heard From', value: client.profile.hear_aboutus },
				{ label: 'Referral', value: client.profile.referral },
				{
					label: 'Documents Uploaded',
					value: client.client_progress.documents_uploaded_date,
				},
				{
					label: 'Documents Method',
					value: client.client_progress.tax_documents_received_method,
				},
			],
		},
		notes: {
			title: 'Notes',
			data: [
				{
					label: 'Additional Notes',
					value: client.client_progress.note || 'No notes',
				},
			],
		},
	});

	const formatCurrency = (amount) => {
		if (!amount && amount !== 0) return 'N/A';
		if (amount < 0) {
			return `$(${Math.abs(amount).toFixed(2)})`;
		}
		return `$${amount.toFixed(2)}`;
	};

	const exportToCsv = async () => {
		try {
			setIsExporting(true);
			const csvFilters = {
				...selectedFilters,
				csv: true,
			};

			await getReport(1, csvFilters, '', true);
			setIsExporting(false);
		}
		catch (error) {
			console.error('Error downloading CSV:', error);
			setIsExporting(false);
		}
	};

	// Quick filter buttons
	const quickFilters = [
		{
			text: 'Full Report',
			filters: { full_report: true },
		},
		{
			text: 'Processed Returns',
			filters: { date_return_not_null: true },
		},
		{
			text: 'Clients Pending',
			filters: { incomplete_return: true },
		},
		{
			text: 'Outstanding Invoices',
			filters: { outstanding_invoice: true },
		},
		{
			text: 'Preparer Incomplete Returns',
			filters: { preparer_incomplete: true },
		},
		{
			text: 'Extension Needed',
			filters: { extension_needed_report: 1 },
		},
		{
			text: 'Franchise Report Needed',
			filters: { franchise_report_needed: 1, date_return_null: true },
		},
		{
			text: 'All Franchise Reports',
			filters: { franchise_report_needed: 1 },
		},
	];

	const renderPagination = () => {
		const maxDisplayedPages = 5;
		let pages = [];
		let startPage = Math.max(
			1,
			pagination.currentPage - Math.floor(maxDisplayedPages / 2)
		);
		let endPage = Math.min(
			pagination.totalPages,
			startPage + maxDisplayedPages - 1
		);

		if (endPage - startPage + 1 < maxDisplayedPages) {
			startPage = Math.max(1, endPage - maxDisplayedPages + 1);
		}

		// Add first page if not in range
		if (startPage > 1) {
			pages.push(
				<Button
					key={1}
					variant="outline"
					size="sm"
					className="mx-1"
					onClick={() => handlePageChange(1)}
				>
					1
				</Button>
			);
			if (startPage > 2) pages.push(<span key="ellipsis1" className="mx-1">...</span>);
		}

		// Add page numbers
		for (let i = startPage; i <= endPage; i++) {
			pages.push(
				<Button
					key={i}
					variant={pagination.currentPage === i ? 'default' : 'outline'}
					size="sm"
					className={`mx-1 ${pagination.currentPage === i ? 'bg-blue-400' : ''}`}
					onClick={() => handlePageChange(i)}
				>
					{i}
				</Button>
			);
		}

		// Add last page if not in range
		if (endPage < pagination.totalPages) {
			if (endPage < pagination.totalPages - 1) {
				pages.push(<span key="ellipsis2" className="mx-1">...</span>);
			}
			pages.push(
				<Button
					key={pagination.totalPages}
					variant="outline"
					size="sm"
					className="mx-1"
					onClick={() => handlePageChange(pagination.totalPages)}
				>
					{pagination.totalPages}
				</Button>
			);
		}

		return (
			<div className="flex items-center justify-center mt-6 space-x-2">
				<Button
					variant="outline"
					size="sm"
					onClick={() => handlePageChange(pagination.currentPage - 1)}
					disabled={pagination.currentPage === 1}
				>
					<ChevronLeft className="h-4 w-4"/>
				</Button>
				{pages}
				<Button
					variant="outline"
					size="sm"
					onClick={() => handlePageChange(pagination.currentPage + 1)}
					disabled={pagination.currentPage === pagination.totalPages}
				>
					<ChevronRight className="h-4 w-4"/>
				</Button>
			</div>
		);
	};

	const renderDataGroup = (group) => (
		<div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
			{group.title && (
				<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2">
					{group.title}
				</h4>
			)}
			<div
				className={`${
					group.data.length <= 1 ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3'
				} grid gap-4`}
			>
				{group.data.map((item, index) => (
					<div key={index} className="space-y-1">
						<p className="text-xs font-medium text-gray-500 dark:text-gray-400">
							{item.label}
						</p>
						<p className="text-sm text-gray-900 dark:text-white">
							{item.value === 0
							 ? 'No'
							 : item.value === 1
							   ? 'Yes'
							   : item.value === null
							     ? 'N/A'
							     : item.value}
						</p>
					</div>
				))}
			</div>
		</div>
	);

	if (isLoading) {
		return <LoadingScreen/>;
	}

	return (
		<div className="w-full min-h-screen bg-white dark:bg-gray-900 page-container z-10 relative">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<div className="flex justify-between items-center mb-8">
					<div>
						<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
							{headerText} {selectedFilters?.year ? `(${selectedFilters?.year})` : ''}
						</h1>
						<p className="text-gray-500 dark:text-gray-400">
							Total Clients: {pagination.total} (Page {pagination.currentPage} of{' '}
							{pagination.totalPages})
						</p>
					</div>
					<div className="flex gap-4">
						{filters && selectedFilters && (
							<ReportFilters
								filters={filters}
								selectedFilters={selectedFilters}
								onApplyFilters={handleApplyFilters}
							/>
						)}
						<button
							className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
							onClick={exportToCsv}
						>
							{isExporting ? (
								<span className={`flex flex-row gap-2 items-center`}><Loader2 className="w-4 h-4 animate-spin"/> Exporting...</span>
							) : (
								 <>
									 <Download className="w-5 h-5 mr-2"/>
									 Export CSV
								 </>
							 )}
						</button>
					</div>
				</div>

				{/* Quick Filter Buttons */}
				<div className="flex flex-wrap gap-3 mb-6">
					{quickFilters.map((filter, index) => (
						<button
							key={index}
							onClick={() => handleApplyFilters(filter.filters, filter.text)}
							className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors border-gray-300 border-2
                ${
								activeFilter === JSON.stringify(filter.filters)
								? 'bg-blue-600 text-white hover:bg-blue-700'
								: 'bg-white dark:bg-gray-800 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
							}`}
						>
							{filter.text}
						</button>
					))}
				</div>

				{/** Filter Pills (for ReportFilters) */}
				{/*<FilterPills
                    filters={filters}
                    selectedFilters={selectedFilters}
                    quickFilters={quickFilters}
                    onRemoveFilter={handleRemoveFilter}
                /> /*}


                {/* Search */}

				<div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6 sticky top-0">
					<div className="relative">
						<input
							type="text"
							placeholder="Search clients..."
							className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
							value={searchTerm}
							onChange={handleSearch}
						/>
						<Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"/>
					</div>
				</div>

				{/* SORTING */}
				<div
					className="col-span-4 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase flex items-center cursor-pointer"
					onClick={() => handleSort('client')}
				>
					<div className={`flex flex-row items-center mb-3`}>
						Client Name
						{sortField === 'client' && (
							<span className="ml-1 flex flex-row">
                                    {sortDirection === 'asc' ? (
	                                    <div className={`flex flex-row`}>
		                                    <ArrowDown className="h-4 w-4"/>
		                                    <span>A-Z</span>
	                                    </div>

                                    ) : (
	                                     <div className={`flex flex-row`}>
		                                     <ArrowUp className="h-4 w-4"/>
		                                     <span>Z-A</span>
	                                     </div>
                                     )}
                                </span>
						)}

						{sortField !== 'client' && (
							<span className="ml-1 flex flex-row">- Click to Sort</span>
						)}
					</div>

				</div>

				{/* Client Cards */}
				<div className="space-y-4">
					{data.map((client, index) => {
						const dataGroups = createDataGroups(client);
						return (
							<div
								key={index}
								onClick={() => toggleClientExpansion(client.id)}
								className={`shadow rounded-lg overflow-hidden cursor-pointer ${
									expandedClients.has(client.id)
									? 'dark:bg-gray-600 bg-gray-100'
									: 'bg-white dark:bg-gray-800'
								}`}
							>
								{/* Always Visible Header Section */}
								<div className="p-4">
									<div className="flex justify-between items-start">
										<div className="space-y-1">
											<div className="flex items-center space-x-2">
												<Link to={`/client-progress/${client.id}?year=${selectedFilters?.year}`}>
													<h3
														className={`text-lg font-medium text-gray-900 dark:text-white`}
													>
														{client.profile.client}
													</h3>
												</Link>
												{client.client_progress.date_return ? (
													<Check className="w-5 h-5 text-green-500"/>
												) : client.client_progress.tax_started ? (
													<Clock className="w-5 h-5 text-yellow-500"/>
												) : null}
											</div>
											<p className="text-sm text-gray-500 dark:text-gray-400">
												{client.email}
											</p>
										</div>
										<button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
											{expandedClients.has(client.id) ? (
												<ChevronUp className="w-6 h-6"/>
											) : (
												 <ChevronDown className="w-6 h-6"/>
											 )}
										</button>
									</div>

									{/* Essential Info Grid */}
									{renderDataGroup(dataGroups.essentialInfo)}
								</div>

								{/* Expandable Sections */}
								{expandedClients.has(client.id) && (
									<div className="border-t dark:border-gray-700 p-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											{Object.entries(dataGroups)
											       .filter(([key]) => key !== 'essentialInfo')
											       .map(([key, group]) => (
												       <div key={key} className="col-span-1">
													       {renderDataGroup(group)}
												       </div>
											       ))}
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				{renderPagination()}
			</div>
		</div>
	);
};

export default Report;
